import { useCallback, useEffect, useRef } from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { EditorActions } from '@atlaskit/editor-core';

import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import {
	useSmartCreationModalActions,
	type OpenModalParams,
} from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

/**
 * Returns an `openSmartCreationModal` callback that, when called from the
 * `editorQuickInsert` entry point, wraps the submit callback to also insert
 * a hyperlink into the editor at the current cursor position.
 */
export const useOpenSmartCreationModalFromQuickInsert = (
	editorActions: EditorActions,
): (({ entryPoint, spaceKey, parentContent, content }: OpenModalParams) => void) => {
	const [{ fullPageEditorPresetAPI }] = useFullPageEditorApi();
	// Use a ref so the submit callback always reads the latest API value at invocation time,
	// rather than the stale value captured when the useCallback was last created.
	const fullPageEditorPresetAPIRef = useRef(fullPageEditorPresetAPI);
	useEffect(() => {
		fullPageEditorPresetAPIRef.current = fullPageEditorPresetAPI;
	}, [fullPageEditorPresetAPI]);
	const { openModal: openSmartCreationModalBase } = useSmartCreationModalActions();

	return useCallback(
		({ entryPoint, spaceKey, parentContent, content }: OpenModalParams) => {
			if (entryPoint === 'editorQuickInsert') {
				const submitWithInsertLink = async ({
					baseUrl,
					path,
				}: {
					baseUrl: string;
					path: string;
				}) => {
					const fullUrl = baseUrl + path;
					const view = editorActions._privateGetEditorView?.();

					// insert hyperlink at the current cursor position
					if (view && fullPageEditorPresetAPIRef.current?.hyperlink?.actions?.insertLink) {
						const { from } = view.state.selection;
						const cmd = fullPageEditorPresetAPIRef.current.hyperlink.actions.insertLink(
							INPUT_METHOD.TYPEAHEAD, // inputMethod — triggered via the `/` quick insert typeahead menu
							from, // from — start position at current cursor
							from, // to — same as from, no text selected
							fullUrl, // href — the URL to link to
							undefined, // title — no title, falls back to displayText
							fullUrl, // displayText — show the URL as the link text in the editor
							true, // cardsAvailable — attempt to resolve as a smart link
						);
						cmd(view.state, view.dispatch);
					}

					window.open(fullUrl, '_blank', 'noopener,noreferrer');
				};

				openSmartCreationModalBase({
					entryPoint,
					spaceKey,
					parentContent,
					content,
					submitCallback: submitWithInsertLink,
				});
			}
		},
		[openSmartCreationModalBase, editorActions],
	);
};
